import { DropdownGroup, DropdownOption } from "../components/MultiSelectDropdown";
import { AttachSquare, Calendar2, Call, Global, Map1, Sms, TextBlock } from "iconsax-reactjs";

export type KybStatusStat = {
  label: string;
  value: number;
  color: string;
};

export type CountryDistributionStat = {
  label: string;
  value: number;
  color: string;
};


export const INFO_TYPES = [
  { value: "TEXT", label: "Text", icon: TextBlock },
  { value: "ATTACHMENT", label: "Attachment", icon: AttachSquare },
  { value: "DATE", label: "Date", icon: Calendar2 },
  { value: "EMAIL", label: "Email address", icon: Sms },
  { value: "PHONE_NUMBER", label: "Phone number", icon: Call },
  { value: "COUNTRY", label: "Country", icon: Global },
  { value: "ADDRESS", label: "Address", icon: Map1 },
];


export const CATEGORY_TITLE_MAP: Record<string, string> = {
  BUSINESS_DETAILS: "Business details",
  CONTACT_INFORMATION: "Contact",
  COMPLIANCE: "Compliance",
  WALLET_INFORMATION: "Wallets",
  UBO: "UBO",
  DIRECTOR: "Director's information",
};

export const KYB_STATUS_STATS: KybStatusStat[] = [
  { label: "Started", value: 25, color: "#FFC44D" },
  { label: "Under review", value: 25, color: "#B7A0FF" },
  { label: "Approved", value: 37, color: "#ADF0A5" },
  { label: "Rejected", value: 13, color: "#FF9698" },
];

export const COUNTRY_DISTRIBUTION_STATS: CountryDistributionStat[] = [
  { label: "NG", value: 50, color: "#7C3AED" },
  { label: "UK", value: 20, color: "#B79BFF" },
  { label: "US", value: 15, color: "#E4DDFF" },
  { label: "Others", value: 15, color: "#9F6BFF" },
];

export const KYB_REGISTRATION_SUMMARY = {
  total: "1,324",
  growth: "+3%",
  growthLabel: "since last month",
};

export const KYB_APPROVAL_SUMMARY = {
  averageApprovalTime: "30 minutes",
  averageProcessingTime: "12 hours avg. in processing",
};

export interface OptionGeneratorFuncInterface {
  options: never[] | DropdownOption[];
  groups: DropdownGroup[] | never[];
  optionMetaMap: Map<string, {
    key: string;
    subtype?: string;
    targetId?: string;
    targetEmail?: string;
  }>;
}

export const OptionGeneratorFunc = ({ businessCatalogue, detail, isAddInfo = false }: { businessCatalogue: any, detail: any, isAddInfo?: boolean }): OptionGeneratorFuncInterface => {
  const updateInfoRaw = isAddInfo
    ? (businessCatalogue as any)?.data?.additionalInformation
    : ((businessCatalogue as any)?.data?.data?.updateInformation ?? (businessCatalogue as any)?.data?.updateInformation);

  const metaMap = new Map<
    string,
    { key: string; subtype?: string; targetId?: string; targetEmail?: string }
  >();

  // If updateInfo is an array, transform it into an object grouped by groupType
  const updateInfo = Array.isArray(updateInfoRaw)
    ? updateInfoRaw.reduce((acc: Record<string, any[]>, item: any) => {
      const groupKey = item.groupType || "OTHER";
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(item);
      return acc;
    }, {})
    : updateInfoRaw;

  if (updateInfo && typeof updateInfo === "object") {
    const parsedGroups: DropdownGroup[] = Object.entries(updateInfo as Record<string, any[]>).map(([catKey, items]) => {
      const title =
        CATEGORY_TITLE_MAP[catKey] ||
        catKey
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase());

      const itemList = Array.isArray(items) ? items : [];
      const groupOptions: DropdownOption[] = itemList.map((item: any, idx: number) => {
        const optionValue = item.targetId
          ? `${catKey}:${item.key}:${item.targetId}`
          : item.targetEmail
            ? `${catKey}:${item.key}:${item.targetEmail}`
            : `${catKey}:${item.key}:${idx}`;

        metaMap.set(optionValue, {
          key: item.key,
          subtype: catKey,
          targetId: item.targetId,
          targetEmail: item.targetEmail,
        });

        return {
          label: item.label || item.key,
          value: optionValue,
        };
      });

      return {
        key: catKey,
        title,
        hasAllOption: catKey === "DIRECTOR" || catKey === "DIRECTORS",
        options: groupOptions,
      };
    });

    if (!parsedGroups.some((g) => g.options.some((o) => o.value === "other"))) {
      parsedGroups.push({
        key: "OTHER",
        title: "Other",
        options: [{ label: "Other", value: "other" }],
      });
    }

    return { options: [], groups: parsedGroups, optionMetaMap: metaMap };
  }

  const rawCatalogue = (businessCatalogue as any)?.data?.data ?? (businessCatalogue as any)?.data;
  const requirements = (Array.isArray(rawCatalogue) && rawCatalogue.length > 0)
    ? rawCatalogue
    : (detail?.kycRequirements ?? []);

  const filteredOptions: DropdownOption[] = requirements
    .filter((item: any) => {
      if (item.type === "document") {
        return !item.documentUrl || item.documentUrl.trim() === "";
      }
      if (item.type === "field") {
        const hasTextValue = Boolean(item.textValue && item.textValue.trim() !== "");
        const hasDirectors = Boolean(item.directors && item.directors.length > 0);
        const hasUbos = Boolean(item.ubos && item.ubos.length > 0);
        return !hasTextValue && !hasDirectors && !hasUbos;
      }
      return true;
    })
    .map((item: any) => {
      const optionValue = item.value || item.rawKey || item.id || item.key || String(item);
      metaMap.set(optionValue, {
        key: item.key || String(item),
        subtype: item.subtype,
        targetId: item.targetId,
        targetEmail: item.targetEmail,
      });
      return {
        label: item.label || item.key || String(item),
        value: optionValue,
      };
    });

  if (!filteredOptions.some((opt) => opt.value === "other")) {
    filteredOptions.push({ label: "Other", value: "other" });
  }

  return { options: filteredOptions, groups: [], optionMetaMap: metaMap };
}